// Thunks extracted from PlatformModule.js

import _ from "lodash";
import React from "react";
import update from "immutability-helper";
import { LE_ASSESSMENT_TYPE_MAPPING } from "Constants";
import { CURRICULUM_TYPE_UBD } from "Constants/stringConstants";
import {
  navigateToPlatformAccessManager,
  navigateToToolsPortalV2,
  goToRoute,
} from "modules/NavigationModule";
import { isId } from "Utils";
import { Acronym } from "@toddle-edu/ds-web";
import { setJsonItem } from "services/localStorage";
import {
  getCurrentCurriculumProgramType,
  getOrganizationCurriculumPrograms,
  getSelectedCurriculumProgram,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { recordEvent } from "lib/eventTracker";
import {
  AI_MAKERTING_EVENTS,
  CHANNEL_MESSAGE_TYPE,
} from "Login/components/ChildrenPage/components/AIModuleMarketingModal/AIModuleMarketingUtils";
import client from "lib/apolloClient/client";
import { sendChannelMessageMutation } from "../PlatformMutation";
import { supportUserEmailId } from "config";
import {
  setOrganizationCurriculumPrograms,
  setCurrentCurriculumProgram,
  setCurrentPlannerElementSets,
  setAcademicYearSelectedForCurrentCurriculumProgram,
} from "./PlatformActions";
import { getPlannerElementSetsGroupedByType } from "./PlatformUtils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getAssessmentTemplatesFromCache } from "routes/components/ChildrenPage/modules/ChildrenPageGraphqlHelpers";

// Thunks
export const getIsCurrentAcademicYear = () => {
  return (dispatch, getState) => {
    const state = getState();
    return !!_.get(
      state,
      "platform.academicYearSelected.isCurrentAcademicYear",
      true
    );
  };
};

export const isAllowedForCurriculum = ({ allowedCurriculums }) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentCurriculumProgramType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    );
    return _.includes(allowedCurriculums, currentCurriculumProgramType);
  };
};

export const handlePlannerElementSetRename = ({
  updatePlannerElementSetBasicDetails,
  curriculumProgramId,
  plannerElementSetId,
  plannerElementSet,
  elementType,
  name,
}) => {
  return async (dispatch, getState) => {
    await updatePlannerElementSetBasicDetails({
      mutationVariables: {
        input: {
          name,
          setId: plannerElementSetId,
        },
      },
    });
    const currentPlannerElementSets = getState().platform
      .currentPlannerElementSets;
    const updatedPlannerElementSet = {
      ...currentPlannerElementSets,
      [elementType]: { ...plannerElementSet, label: name },
    };
    dispatch(setCurrentPlannerElementSets(updatedPlannerElementSet));

    const organizationCurriculumPrograms = getState().platform
      .organizationCurriculumPrograms;

    const currentProgram = _.find(organizationCurriculumPrograms, {
      id: curriculumProgramId,
    });
    const updatedProgram = update(currentProgram, {
      plannerElementSets: { $set: updatedPlannerElementSet },
    });
    const updatedOrganizationCurriculumPrograms = update(
      organizationCurriculumPrograms,
      {
        [_.indexOf(
          organizationCurriculumPrograms,
          _.find(organizationCurriculumPrograms, { id: curriculumProgramId })
        )]: { $set: updatedProgram },
      }
    );
    dispatch(
      setOrganizationCurriculumPrograms(updatedOrganizationCurriculumPrograms)
    );
  };
};

export const getIsCurriculumProgramIdPathValid = ({
  curriculumPrograms,
  curriculumProgramIdPath,
}) => {
  return (dispatch, getState) => {
    const organizationCurriculumPrograms = getState().platform
      .organizationCurriculumPrograms;

    curriculumPrograms = _.isEmpty(curriculumPrograms)
      ? organizationCurriculumPrograms
      : curriculumPrograms;

    const currentCurriculumProgram = _.find(curriculumPrograms, {
      id: curriculumProgramIdPath,
    });

    return (
      isId(curriculumProgramIdPath) && !_.isEmpty(currentCurriculumProgram)
    );
  };
};

export const setupCurriculumProgramDependencies = ({
  curriculumPrograms,
  curriculumProgramIdPath,
}) => {
  return (dispatch, getState) => {
    const organizationCurriculumPrograms = getState().platform
      .organizationCurriculumPrograms;

    curriculumPrograms = _.isEmpty(curriculumPrograms)
      ? organizationCurriculumPrograms
      : curriculumPrograms;

    const currentCurriculumProgramFromOrganization = _.find(
      organizationCurriculumPrograms,
      ({ id }) => id == curriculumProgramIdPath
    );

    const currentAcademicYear = _.find(
      _.get(currentCurriculumProgramFromOrganization, "academicYears", []),
      {
        isCurrentAcademicYear: true,
      }
    );

    const currentCurriculumProgram = _.find(curriculumPrograms, {
      id: curriculumProgramIdPath,
    }) ?? { id: null, type: null };

    // setup planner element sets
    const plannerElementSets = getPlannerElementSetsGroupedByType({
      organizationCurriculumPrograms,
      curriculumProgramId: currentCurriculumProgram?.id,
    });
    dispatch(setCurrentPlannerElementSets(plannerElementSets));

    // setup curriculum program
    dispatch(setCurrentCurriculumProgram(currentCurriculumProgram));

    dispatch(
      setAcademicYearSelectedForCurrentCurriculumProgram(currentAcademicYear)
    );

    //setup local storage
    setJsonItem({
      key: "currentCurriculumProgram",
      value: _.pick(currentCurriculumProgram, ["id", "type", "label"]),
    });
  };
};

/**
 * Use this function to set current curriculum program in redux
 */
export const setupCurrentCurriculumProgram = curriculumProgramId => {
  return (dispatch, getState) => {
    const organizationCurriculumPrograms = getState().platform
      .organizationCurriculumPrograms;

    const currentCurriculumProgram = _.find(organizationCurriculumPrograms, {
      id: curriculumProgramId,
    });

    // setup planner element sets
    const plannerElementSets = getPlannerElementSetsGroupedByType({
      organizationCurriculumPrograms,
      curriculumProgramId,
    });
    dispatch(setCurrentPlannerElementSets(plannerElementSets));

    // setup curriculum program
    dispatch(setCurrentCurriculumProgram(currentCurriculumProgram));

    // setup local storage
    setJsonItem({
      key: "currentCurriculumProgram",
      value: _.pick(currentCurriculumProgram, ["id", "type", "label"]),
    });
  };
};

/**
 * This is a replacement for LE_ASSESSMENT_TYPE_MAPPING
 * For UBD, it will return the assessment types from redux which are fetched from BE
 * For other curriculum types, it will return LE_ASSESSMENT_TYPE_MAPPING only
 */
export const getCurrentAssessmentTypes = (params = {}) => {
  return (dispatch, getState) => {
    const {
      curriculumType,
      curriculumId,
      isVisibleInGlobalSearch = false,
    } = params;

    const state = getState();
    const currentCurriculumProgramType =
      curriculumType ?? getCurrentCurriculumProgramType(state);

    // Check if FF is enabled for fetching dynamic assessment types
    if (getIsAssessmentTypeConfigurationEnabled()) {
      const selectedCurriculumId =
        curriculumId ?? getSelectedCurriculumId(state);

      if (selectedCurriculumId) {
        const assessmentTemplatesData = getAssessmentTemplatesFromCache(
          selectedCurriculumId
        );
        const taskTypes = _.get(assessmentTemplatesData, "taskTypes", []);

        if (!_.isEmpty(taskTypes)) {
          // Transform taskTypes data to match the expected assessment types structure
          const transformedAssessmentTypes = _.reduce(
            taskTypes,
            (result, taskType) => {
              const { id, type, label, color } = taskType;
              return {
                ...result,
                [id]: {
                  icon: Acronym,
                  label,
                  localeLabel: label,
                  locale: label,
                  color,
                  isEnabled: true,
                  value: id,
                  uid: id,
                },
              };
            },
            {}
          );

          return transformedAssessmentTypes;
        }
      }
    }

    switch (currentCurriculumProgramType) {
      case CURRICULUM_TYPE_UBD: {
        const ubdAssessmentTypes = isVisibleInGlobalSearch
          ? _.get(
              state,
              [
                "app_services",
                "globalSearchBar",
                "ubdAssessmentTypes",
                curriculumId,
              ],
              {}
            )
          : _.get(state, "platform.ubdAssessmentTypes", []);

        const allUbdAssessmentTypes = {
          ...LE_ASSESSMENT_TYPE_MAPPING,
          ...ubdAssessmentTypes,
        };
        return _.reduce(
          allUbdAssessmentTypes,
          (result, value, uid) => {
            // merging assessment type object from redux with default assessment type object
            // this is done in order to get icons for existing assessment types because icons won't be available in redux
            // and we will not add icons for new assessment types created in future
            // for new assessment types created in future, consider icon as DS Acronym component and pass props accordingly
            const defaultAssessmentTypeObj =
              _.get(LE_ASSESSMENT_TYPE_MAPPING, uid, {}) || {};
            return {
              ...result,
              [uid]: { icon: Acronym, ...defaultAssessmentTypeObj, ...value },
            };
          },
          {}
        );
      }
      default:
        return LE_ASSESSMENT_TYPE_MAPPING;
    }
  };
};

/**
 * navigate user to specific action on successfull verification of the session
 */
export const handleNavigateWithSessionCheck = () => {
  return (dispatch, getState) => {
    const loginState = _.get(getState(), "login");
    const action = _.get(loginState, "sessionVerificationDetails.action");

    switch (action) {
      case "PLATFORM_ACCESS_MANAGER": {
        dispatch(navigateToPlatformAccessManager());
        break;
      }
      case "SWITCH_TO_TOOL_V2": {
        dispatch(navigateToToolsPortalV2());
        break;
      }
      case "INTERNAL_PORTAL": {
        dispatch(navigateToPlatformAccessManager());
        break;
      }
      case "SWITCH_ORGANIZATION": {
        dispatch(
          goToRoute({
            route: "platform/switch-organization",
          })
        );
        break;
      }
    }
  };
};

export const sendChannelMessageForAIMarketing = (
  clickedOnType,
  meetingData = {}
) => {
  return (dispatch, getState) => {
    const state = getState();

    const moduleKey =
      _.get(
        state,
        "platform.aiModuleMarketingKey",
        _.get(meetingData, "module", "")
      ) || _.get(meetingData, "module", "");

    const moduleName = _.capitalize(_.replace(moduleKey, "_", " "));
    const userInfo = _.get(state, "login.userInfo", {});

    const userName = `${_.get(userInfo, "first_name", "")} ${_.get(
      userInfo,
      "middle_name",
      ""
    )} ${_.get(userInfo, "last_name", "")}`;
    const userEmail = _.get(userInfo, "email", "");

    const orgName = _.get(userInfo, "org_name", "");
    const orgId = _.get(userInfo, "org_id", "");
    const selectedCurriculumProgram = getSelectedCurriculumProgram(state);

    const programs = getOrganizationCurriculumPrograms(state);

    const currentCurriculumProgram = _.find(programs, {
      id: selectedCurriculumProgram?.id,
    });
    const programName = !_.isEmpty(currentCurriculumProgram)
      ? `[${_.get(currentCurriculumProgram, "acronym")}] ${_.get(
          currentCurriculumProgram,
          "label",
          ""
        )}`
      : "No Curriculum Program Selected";

    const userRole = _.capitalize(_.get(userInfo, "role", ""));

    recordEvent({
      eventName: clickedOnType,
      eventData: {
        module: moduleKey,
        userName,
        userEmail,
        orgName,
        userRole,
        curriculum: programName,
        curriculumId: _.get(currentCurriculumProgram, "id", ""),
        fromHomepage:
          clickedOnType === AI_MAKERTING_EVENTS.AI_TUTOR_BOOK_DEMO_CLICKED ||
          clickedOnType === AI_MAKERTING_EVENTS.AI_TUTOR_EXPLORE_MORE_CLICKED ||
          clickedOnType ===
            AI_MAKERTING_EVENTS.HOMEPAGE_MARKETING_BANNER_CLICKED ||
          moduleKey === "HOMEPAGE",
      },
    });

    // Heading message will be different in case of meeting scheduled:
    // message when meeting is scheduled: *{userRole}* Booked a demo in *`{moduleName}`*
    // message when button is clicked: *{userRole}* clicked on *`{buttonName}`* Button in *`{moduleName}`*
    const headingMessage =
      clickedOnType !== AI_MAKERTING_EVENTS.MEETING_BOOKED
        ? `*${userRole}* clicked on *\`${
            clickedOnType === AI_MAKERTING_EVENTS.CHAT_WITH_US_CLICK
              ? "Chat with us"
              : clickedOnType === AI_MAKERTING_EVENTS.AI_TUTOR_BOOK_DEMO_CLICKED
              ? "Book demo"
              : "Upgrade now"
          }\`* Button in *\`${moduleName}\`*`
        : `*${userRole}* Booked a demo in *\`${moduleName}\`*`;

    // Message blocks format can be referred at: https://api.slack.com/reference/block-kit/blocks
    const messageBlocks = {
      blocks: [
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: headingMessage,
          },
        },
        {
          type: "divider",
        },
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `*Organization:* ${orgName} \`(${orgId})\` \n *Program:* ${programName} \n *User*: ${userName} \n *Email*: ${userEmail}`,
          },
        },
        {
          type: "divider",
        },
      ],
    };

    if (
      clickedOnType === AI_MAKERTING_EVENTS.MEETING_BOOKED &&
      !_.isEmpty(meetingData)
    ) {
      messageBlocks.blocks.push({
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*Meeting Details:* \n *Date & Time:* ${
            meetingData.dateTime
          } (IST) - ${meetingData.duration || ""} minutes \n *Host:* ${
            meetingData.host
          }`,
        },
      });
      messageBlocks.blocks.push({
        type: "divider",
      });
    }

    let messageType = CHANNEL_MESSAGE_TYPE.AI_MARKETING_UPGRADE_NOW;
    switch (clickedOnType) {
      case AI_MAKERTING_EVENTS.CHAT_WITH_US_CLICK:
        messageType = CHANNEL_MESSAGE_TYPE.AI_MARKETING_CHAT_WITH_US;
        break;
      case AI_MAKERTING_EVENTS.MEETING_BOOKED:
        messageType = CHANNEL_MESSAGE_TYPE.AI_MARKETING_MEETING_SCHEDULED;
        break;
      case AI_MAKERTING_EVENTS.AI_TUTOR_EXPLORE_MORE_CLICKED:
      case AI_MAKERTING_EVENTS.AI_TUTOR_BOOK_DEMO_CLICKED:
        messageType = CHANNEL_MESSAGE_TYPE.AI_MARKETING_CHAT_WITH_US;
        break;
    }
    const input = {
      type: messageType,
      metadata: {
        message: messageBlocks,
      },
    };

    if (
      !_.isEqual(userEmail, supportUserEmailId) &&
      _.isEqual(clickedOnType, AI_MAKERTING_EVENTS.MEETING_BOOKED)
    ) {
      // Non-blocking call
      client.mutate({
        mutation: sendChannelMessageMutation,
        variables: {
          input,
        },
      });
    }
  };
};

export const trackAIMarketingModalOpenEvent = moduleKey => {
  return (dispatch, getState) => {
    const userInfo = getUserInfo();
    const state = getState();

    const userName = `${_.get(userInfo, "first_name", "")} ${_.get(
      userInfo,
      "middle_name",
      ""
    )} ${_.get(userInfo, "last_name", "")}`;
    const userEmail = _.get(userInfo, "email", "");
    const orgName = _.get(userInfo, "org_name", "");
    const userRole = _.capitalize(_.get(userInfo, "role", ""));

    const currentCurriculumProgram = _.get(
      state,
      "currentCurriculumProgram",
      {}
    );
    const programName = !_.isEmpty(currentCurriculumProgram)
      ? `[${_.get(currentCurriculumProgram, "acronym")}] ${_.get(
          currentCurriculumProgram,
          "label",
          ""
        )}`
      : "No Curriculum Program Selected";

    recordEvent({
      eventName: AI_MAKERTING_EVENTS.CLICK_AI_BUTTON,
      eventData: {
        module: moduleKey,
        userName,
        userEmail,
        orgName,
        userRole,
        curriculum: programName,
        curriculumId: _.get(currentCurriculumProgram, "id", ""),
      },
    });
  };
};
